// src/constants/newTipV2.js
//
// Constantes du module New TIP v2. Doivent rester alignées avec
// config/new_tip_v2.php côté Laravel.
//
// Les listes de statuts sont volontairement identiques à celles de la v1 :
// c'est pourquoi les composants StatusBadge / KpiCard / MapPreview de
// components/new-tip/ sont réutilisés tels quels.

export const CAMPAIGN_STATUSES = [
  { value: 'draft',  label: 'Brouillon', color: 'grey'      },
  { value: 'active', label: 'Active',    color: 'success'   },
  { value: 'closed', label: 'Clôturée',  color: 'blue-grey' },
];

export const ASSIGNMENT_STATUSES = [
  { value: 'pending',   label: 'En attente', color: 'orange'  },
  { value: 'visited',   label: 'Visitée',    color: 'info'    },
  { value: 'validated', label: 'Validée',    color: 'success' },
  { value: 'rejected',  label: 'Rejetée',    color: 'error'   },
];

export const VISIT_STATUSES = [
  { value: 'submitted', label: 'Soumise',  color: 'info'    },
  { value: 'validated', label: 'Validée',  color: 'success' },
  { value: 'rejected',  label: 'Rejetée',  color: 'error'   },
];

// Types de photos v2 : le planogramme et la PLV sont deux photos distinctes,
// là où la v1 avait un type combiné « planogram_plv ».
export const PHOTO_TYPES = {
  planogram:      { label: 'Planogramme',      min: 1, max: 1 },
  plv:            { label: 'PLV',              min: 1, max: 1 },
  stock_products: { label: 'Stock produits',   min: 1, max: 6 },
  hats_collector: { label: 'Chapeau collector', min: 1, max: 1 },
};

// Nombre de paliers par affectation. Le palier 1 est obligatoire, les
// paliers 2 et 3 facultatifs et contigus.
export const TIERS = [1, 2, 3];
export const REQUIRED_TIERS = 1;

// Filtre « palier atteint » du dashboard. 'none' isole les visites soumises
// sans qu'aucun palier ait été franchi.
export const ACHIEVED_TIER_OPTIONS = [
  { value: 1,      label: 'Palier 1', color: 'orange'  },
  { value: 2,      label: 'Palier 2', color: 'info'    },
  { value: 3,      label: 'Palier 3', color: 'success' },
  { value: 'none', label: 'Aucun',    color: 'grey'    },
];

export const DEFAULT_VALUES = {
  hats_objective_qty: 150,
  hats_gain: 1500,
};

export const MAX_VISITS_PER_ASSIGNMENT = 2;

export function findStatus(list, value) {
  return list.find(s => s.value === value) || { value, label: value, color: 'grey' };
}

/**
 * Libellé du palier atteint par une visite.
 * `achieved_tier` vaut null quand aucun palier n'a été franchi.
 */
export function tierLabel(achievedTier) {
  if (achievedTier === null || achievedTier === undefined) return 'Aucun palier';
  return `Palier ${achievedTier}`;
}

export function tierColor(achievedTier) {
  const found = ACHIEVED_TIER_OPTIONS.find(o => o.value === achievedTier);
  return found ? found.color : 'grey';
}

/**
 * Paliers définis d'une affectation, du plus bas au plus haut.
 * Miroir de NewTipV2CampaignAssignment::definedTiers() côté API : un palier
 * n'est retenu que si sa quantité ET son gain sont renseignés.
 */
export function definedTiers(assignment) {
  if (!assignment) return [];
  return TIERS
    .map(tier => ({
      tier,
      qty: assignment[`objective_${tier}_qty`],
      gain: assignment[`objective_${tier}_gain`],
    }))
    .filter(t => t.qty !== null && t.qty !== undefined && t.qty !== ''
              && t.gain !== null && t.gain !== undefined && t.gain !== '');
}

/**
 * Palier le plus haut atteint pour une quantité en stock donnée, ou null.
 *
 * Reproduit NewTipV2GainCalculatorService::resolveTier() pour l'affichage
 * côté back-office (simulation, prévisualisation). Le gain réellement accordé
 * reste celui figé en base à la soumission — ne jamais recalculer un gain
 * historique avec cette fonction.
 */
export function resolveTier(assignment, stockQty) {
  const tiers = definedTiers(assignment);
  for (let i = tiers.length - 1; i >= 0; i -= 1) {
    if (Number(stockQty) >= Number(tiers[i].qty)) return tiers[i];
  }
  return null;
}
